// Highlight-snippet parser — XSS-safe render prep for search results
// (RFC-0011 Task 1.4, PD-0017 P5). Mirrors the `url-scheme.ts` ethos: a pure,
// exhaustively-testable `lib/*` guard that never trusts upstream content.
//
// `GET /api/search` returns each hit's `snippet` as Meilisearch's cropped
// excerpt with inline `<mark>`/`</mark>` highlight tags around matched terms.
// That string is UNTRUSTED repository content: besides the mark tags it can
// contain literal `<script>`, `<img onerror=…>`, or even a literal `<mark>`
// authored in the doc. This parser splits the snippet on the FIXED literal
// `<mark>`/`</mark>` delimiters only and returns plain-text segments — it emits
// no HTML. `SearchResultItem` maps segments to React text nodes and `<mark>`
// elements, so every non-delimiter character (including `<script>`) renders as
// inert text. The UI must NEVER pass a snippet to `dangerouslySetInnerHTML`.

const OPEN = '<mark>';
const CLOSE = '</mark>';

/**
 * One run of snippet text. `marked` runs sit between a matched `<mark>…</mark>`
 * pair (render as `<mark>`); everything else is inert text (render as a text
 * node). `text` is always a verbatim substring of the input — never HTML.
 */
export interface HighlightSegment {
  text: string;
  marked: boolean;
}

/**
 * Split a search `snippet` into ordered `{ text, marked }` segments on the fixed
 * literal `<mark>`/`</mark>` delimiters. Returns data (not JSX) so it stays a
 * pure, 100%-testable function.
 *
 * Guarantees:
 * - Only the exact literal `<mark>`/`</mark>` byte sequences are treated as
 *   delimiters; all other characters (`<script>`, `<img onerror=…>`, `&`, …)
 *   remain inert text in a segment.
 * - Unbalanced / nested delimiters never throw: an unmatched `<mark>` (no later
 *   `</mark>`) is emitted verbatim as inert text.
 * - A literal `<mark>…</mark>` authored in doc content is indistinguishable from
 *   a Meilisearch highlight and will render highlighted (a documented, benign
 *   ambiguity — it is still text, never executable HTML).
 */
export function parseHighlightSegments(snippet: string): HighlightSegment[] {
  const segments: HighlightSegment[] = [];

  // Append text, coalescing consecutive runs with the same `marked` flag so the
  // output is minimal and stable.
  const push = (text: string, marked: boolean): void => {
    if (text.length === 0) return;
    const last = segments[segments.length - 1];
    if (last && last.marked === marked) last.text += text;
    else segments.push({ text, marked });
  };

  let i = 0;
  while (i < snippet.length) {
    const open = snippet.indexOf(OPEN, i);
    if (open === -1) {
      push(snippet.slice(i), false);
      break;
    }
    // Text preceding the opening delimiter is inert.
    push(snippet.slice(i, open), false);

    const contentStart = open + OPEN.length;
    const close = snippet.indexOf(CLOSE, contentStart);
    if (close === -1) {
      // Unbalanced open: emit the remainder (incl. the literal "<mark>") inert.
      push(snippet.slice(open), false);
      break;
    }

    push(snippet.slice(contentStart, close), true);
    i = close + CLOSE.length;
  }

  return segments;
}
